(ns ui.tree
  "Compound primitives for tree views.

  Compose `tree-node` (Collapsible.Root wrapper), `tree-row-trigger`
  (the clickable row) and `tree-node-content` (the toggle-able content)
  — the same way you'd compose Radix primitives. The row's content
  (icon, label, secondary label) is composed as children of the trigger:

      [tree-node {:open o :on-open-change ...}
       [tree-row-trigger {:active a}
        [tree-row-icon SomeIcon]
        [tree-row-label \"name\"]
        [tree-row-secondary-label \"3 tables\"]]
       [tree-node-content
        [child-tree-node-1]
        [child-tree-node-2]]]

  Callers own the data model, fetching, and selection logic.
  See `use-cached-open-state` for a hook that persists open state."
  (:require
   ["@radix-ui/react-collapsible" :as Collapsible]
   ["@radix-ui/react-slot" :as Slot]
   ["lucide-react" :refer [ChevronRight]]
   [applied-science.js-interop :as j]
   [react :as react]
   [ui.utils :refer [clsx separate-props]]))

(defn use-cached-open-state
  "React hook returning `[open on-open-change]` backed by `cache` (a plain JS
  object, e.g. `#js {}`) keyed by the string `cache-key`. The cache persists
  across unmounts so a tree's open state is preserved when its parent is
  hidden and shown again.

  When opened, stores `open-payload` under `cache-key` (default true); when
  closed, stores false. Pass a payload — e.g. the node — when callers need
  to enumerate currently-open entries via `js/Object.values`."
  ([cache cache-key]
   (use-cached-open-state cache cache-key true))
  ([cache cache-key open-payload]
   (let [[open set-open] (react/useState
                          (fn [] (boolean (j/get cache cache-key))))]
     [open
      (fn [v]
        (let [v' (boolean v)]
          (j/assoc! cache cache-key (if v' open-payload false))
          (set-open v')))])))

(defn tree-row-icon
  [icon]
  [:> icon {:size  16
            :class "text-kleene-icon opacity-90 shrink-0 leading-zero align-top"}])

(defn tree-row-label
  [& args]
  (let [[props children] (separate-props args)]
    (into [:span (-> props
                     (update :class clsx "min-w-0 whitespace-nowrap overflow-hidden text-ellipsis group-hover:opacity-60 transition-opacity"))]
          children)))

(defn tree-row-secondary-label
  [& args]
  (let [[props children] (separate-props args)]
    (into [:small (-> props
                      (update :class clsx "ml-auto whitespace-nowrap text-[10px] text-kleene-dark-05 dark:text-kleene-neutral-01 shrink-0"))]
          children)))

(defn tree-row-trigger
  "Clickable row used as the trigger of a `tree-node`. Renders a
  Collapsible.Trigger for branch rows, or a plain div for leaf rows.
  Compose icon, label, and secondary label as children using
  `tree-row-icon`, `tree-row-label`, and `tree-row-secondary-label`.

  Props:
   :active          — selected-row highlight
   :leaf            — when true, no chevron and a plain div instead of
                      Collapsible.Trigger
   :on-click, :on-double-click
   :row-class, :data-cy"
  [& args]
  (let [[{:keys [leaf active on-click on-double-click row-class data-cy]} children]
        (separate-props args)
        class   (clsx "group bg-kleene-background flex items-center cursor-pointer py-0.5 w-full text-left"
                      row-class)
        body    (into [:header
                       {:class (clsx "group/tree-row flex gap-1 items-center w-full overflow-hidden text-kleene-text"
                                     (when active "bg-kleene-selected-background ring ring-kleene-background rounded-lg"))}
                       (if leaf
                         [:span {:class "h-5 w-5 shrink-0"}]
                         [:> ChevronRight {:size  20
                                           :class "text-kleene-icon shrink-0 transition-transform group-data-[state=open]/tree-row:rotate-90"}])]
                      children)]
    (if leaf
      [:> Slot/Root {:class           class
                     :on-click        on-click
                     :on-double-click on-double-click
                     :data-cy         data-cy}
       body]
      [:> Collapsible/Trigger
       {:as-child        true
        :class           class
        :on-click        on-click
        :on-double-click on-double-click
        :data-cy         data-cy}
       body])))

(defn tree-node
  "Wraps a `tree-row-trigger` and a `tree-node-content`. Thin wrapper
  around Radix Collapsible.Root. Leaf nodes simply omit the
  `tree-node-content` and pass `:leaf true` to their `tree-row-trigger`.

  Props:
   :open            — controlled open state (boolean)
   :on-open-change  — fn called with new boolean
   :class           — applied to the outer wrapper"
  [& args]
  (let [[props children] (separate-props args)
        {:keys [open on-open-change class]} props]
    (into [:> Collapsible/Root
           {:open           (boolean open)
            :on-open-change (or on-open-change (fn [_]))
            :class          (clsx "relative" class)}]
          children)))

(defn tree-node-content
  "Children that mount only while the parent `tree-node` is open.
  Thin wrapper around Radix Collapsible.Content."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:> Collapsible/Content props] children)))
